
import { useState } from 'react';
import Button from '../../../components/base/Button';

export default function ContactSection() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    age: '',
    currentHousing: '',
    budget: '',
    timeline: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      // application/x-www-form-urlencoded形式でデータを準備
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d3k8hu3pu5c6jrqf527g', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          phone: '',
          age: '',
          currentHousing: '',
          budget: '',
          timeline: '',
          message: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-20 bg-gray-50">
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-gray-900 mb-6">
            資料請求・無料相談
          </h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            「ちょこっとハウス」について詳しく知りたい方は、<br />
            お気軽にお問い合わせください。
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* お問い合わせフォーム */}
          <div className="bg-white rounded-2xl p-8 shadow-lg">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">
              <i className="ri-mail-line mr-2 text-orange-500"></i>
              お問い合わせフォーム
            </h3>
            
            <form onSubmit={handleSubmit} data-readdy-form id="contact-form">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                    お名前 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
                    placeholder="山田太郎"
                  />
                </div>
                
                <div>
                  <label htmlFor="age" className="block text-sm font-medium text-gray-700 mb-2">
                    年代
                  </label>
                  <select
                    id="age"
                    name="age"
                    value={formData.age}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 pr-8 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-5
                    00 focus:border-transparent text-sm"
                  >
                    <option value="">選択してください</option>
                    <option value="40代">40代</option>
                    <option value="50代">50代</option>
                    <option value="60代">60代</option>
                    <option value="70代以上">70代以上</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                    メールアドレス <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
                    placeholder="example@email.com"
                  />
                </div>
                
                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                    電話番号
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
                    placeholder="090-1234-5678"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label htmlFor="currentHousing" className="block text-sm font-medium text-gray-700 mb-2">
                    現在のお住まい
                  </label>
                  <select
                    id="currentHousing"
                    name="currentHousing"
                    value={formData.currentHousing}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 pr-8 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
                  >
                    <option value="">選択してください</option>
                    <option value="持ち家（戸建て）">持ち家（戸建て）</option>
                    <option value="持ち家（マンション）">持ち家（マンション）</option>
                    <option value="賃貸（アパート）">賃貸（アパート）</option>
                    <option value="賃貸（マンション）">賃貸（マンション）</option>
                    <option value="その他">その他</option>
                  </select>
                </div>
                
                <div>
                  <label htmlFor="budget" className="block text-sm font-medium text-gray-700 mb-2">
                    ご予算
                  </label>
                  <select
                    id="budget"
                    name="budget"
                    value={formData.budget}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 pr-8 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
                  >
                    <option value="">選択してください</option>
                    <option value="500万円以下">500万円以下</option>
                    <option value="500-800万円">500-800万円</option>
                    <option value="800-1000万円">800-1000万円</option>
                    <option value="1000万円以上">1000万円以上</option>
                    <option value="未定">未定</option>
                  </select>
                </div>
              </div>

              <div className="mb-4">
                <label htmlFor="timeline" className="block text-sm font-medium text-gray-700 mb-2">
                  建築希望時期
                </label>
                <select
                  id="timeline"
                  name="timeline"
                  value={formData.timeline}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 pr-8 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
                >
                  <option value="">選択してください</option>
                  <option value="3ヶ月以内">3ヶ月以内</option>
                  <option value="半年以内">半年以内</option>
                  <option value="1年以内">1年以内</option>
                  <option value="1年以上先">1年以上先</option>
                  <option value="未定">未定</option>
                </select>
              </div>

              <div className="mb-6">
                <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                  ご質問・ご要望
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  rows={4}
                  maxLength={500}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm resize-none"
                  placeholder="ご質問やご要望がございましたら、お気軽にお書きください。（500文字以内）"
                />
                <div className="text-right text-sm text-gray-500 mt-1">
                  {formData.message.length}/500文字
                </div>
              </div>

              {submitStatus === 'success' && (
                <div className="mb-4 p-4 bg-green-100 border border-green-400 text-green-700 rounded-lg">
                  <i className="ri-check-circle-line mr-2"></i>
                  お問い合わせありがとうございます。担当者より折り返しご連絡いたします。
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="mb-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg">
                  <i className="ri-error-warning-line mr-2"></i>
                  送信に失敗しました。しばらく時間をおいて再度お試しください。
                </div>
              )}

              <Button
                type="submit"
                size="lg"
                disabled={isSubmitting || formData.message.length > 500}
                className="w-full"
              >
                {isSubmitting ? (
                  <>
                    <i className="ri-loader-4-line mr-2 animate-spin"></i>
                    送信中...
                  </>
                ) : (
                  <>
                    <i className="ri-send-plane-line mr-2"></i>
                    資料請求・無料相談を申し込む
                  </>
                )}
              </Button>
            </form>
          </div>

          {/* お問い合わせ情報 */}
          <div className="space-y-8">
            <div className="bg-white rounded-2xl p-8 shadow-lg">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">
                <i className="ri-phone-line mr-2 text-orange-500"></i>
                お電話でのお問い合わせ
              </h3>
              <div className="space-y-4">
                <p className="text-gray-700">
                  お急ぎの方は、お電話でも承ります。<br />
                  平日 9:00-18:00（土日祝除く）
                </p>
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center">
                    <i className="ri-customer-service-2-line text-xl text-orange-500"></i>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">湯沢開発株式会社</p>
                    <p className="text-lg font-bold text-gray-900">お電話でのご相談も承ります</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-lg">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">
                <i className="ri-map-pin-line mr-2 text-orange-500"></i>
                対応エリア
              </h3>
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                    <i className="ri-building-line text-xl text-blue-500"></i>
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900">湯沢市</h4>
                    <p className="text-gray-600 text-sm">湯沢市全域でサービスを提供</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-3">
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                    <i className="ri-building-line text-xl text-green-500"></i>
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900">横手市</h4>
                    <p className="text-gray-600 text-sm">横手市全域でサービスを提供</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-orange-500 to-red-500 rounded-2xl p-8 text-white">
              <h3 className="text-2xl font-bold mb-4">
                <i className="ri-gift-line mr-2"></i>
                無料サービス
              </h3>
              <ul className="space-y-3">
                <li className="flex items-center">
                  <i className="ri-check-line mr-3 text-xl"></i>
                  詳細資料の送付
                </li>
                <li className="flex items-center">
                  <i className="ri-check-line mr-3 text-xl"></i>
                  現地見学会のご案内
                </li>
                <li className="flex items-center">
                  <i className="ri-check-line mr-3 text-xl"></i>
                  建築費用のお見積もり
                </li>
                <li className="flex items-center">
                  <i className="ri-check-line mr-3 text-xl"></i>
                  資金計画のご相談
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
